import { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import {
  CheckboxWithLabel,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormFieldError,
  TextArea,
} from '@retailer-platform/shared-components'
import { Button, Link, typography } from '@retailer-platform/shared-components/src/tds'
import { errors } from '../../../../utils/error-handling/errors'
import { useSurvey } from '../RetailerSatisfactionSurveyContext'
import { ButtonsSection } from '../RetailerSatisfactionSurveyComponents'
import { useDashMessages } from '../../../../intl/intl.hooks'
import { useTrackEvent } from '../../../../utils/events/useTrackEvent.hook'
import {
  UserFeedbackSurveyPages,
  type UserFeedbackSurveyState,
  type UserFeedbackSurveyProps,
} from './UserFeedbackSurveyController'

const SurveyInputContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '8px',
  marginBottom: '18px',
})

const SurveySubheading = styled.div({
  ...typography.bodyEmphasized,
})

const SurveyHelpText = styled.div({
  marginBottom: '12px',
  fontSize: '14px',
})

const UserFeedbackSurveyPage1 = () => {
  const [errorMessage, setErrorMessage] = useState(null)

  const {
    surveyState,
    updateSurveyState,
    submitSurvey,
    isSurveySubmitted,
    resetSurveySubmission,
    error,
    loading,
    goToPage,
    toolProps,
  } = useSurvey<UserFeedbackSurveyPages, UserFeedbackSurveyState, UserFeedbackSurveyProps>()

  const messages = useDashMessages({
    title: 'userFeedback.survey.page1.title',
    textAreaSubheading: 'userFeedback.survey.page1.textArea.subheading',
    textAreaHelpText: 'userFeedback.survey.page1.textArea.helpText',
    textAreaSupportText: 'userFeedback.survey.page1.textArea.supportText',
    textAreaSupportTextLink: 'userFeedback.survey.page1.textArea.supportTextLink',
    textAreaCustomerSupportText: 'userFeedback.survey.page1.textArea.customerSupportText',
    textAreaCustomerSupportLink: 'userFeedback.survey.page1.textArea.customerSupportLink',
    textAreaPlaceholder: 'userFeedback.survey.page1.textArea.placeholder',
    checkboxLabel: 'userFeedback.survey.page1.checkbox.label',
    secondaryButtonText: 'userFeedback.survey.page1.secondaryButton',
    primaryButtonText: 'userFeedback.survey.page1.primaryButton',
    errorMessageFallback: 'userFeedback.survey.page1.errorMessage',
  })

  const isButtonDisabled = surveyState.userFeedbackResponse.length === 0

  const trackEvent = useTrackEvent()

  useEffect(() => {
    if (isSurveySubmitted) {
      goToPage(UserFeedbackSurveyPages.Page2)
      trackEvent({
        id: 'app.userFeedback.survey.submitSuccess',
        description: 'Tracks whenever the user feedback survey is succesfully submitted',
      })
    }
  }, [isSurveySubmitted, goToPage, trackEvent])

  useEffect(() => {
    /**
     * Incase the survey submission fails, we:
     * 1. Show the error message on the form
     * 2. Clear the survey submission status, so the user can try again
     * 3. Log the error for internal tracking
     */
    if (error?.message && !errorMessage) {
      const formattedErrorMessage = `${messages.errorMessageFallback} ${error.message}`

      // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
      setErrorMessage(formattedErrorMessage)
      resetSurveySubmission()
      errors.captureException(error, `User Feedback Survey with error message + ${error.message}`)
      trackEvent({
        id: 'app.userFeedback.survey.submitError',
        description: 'Tracks whenever an error occurs, when submitting the feedback survey',
      })
    }
  }, [
    error,
    error?.message,
    errorMessage,
    messages.errorMessageFallback,
    resetSurveySubmission,
    trackEvent,
  ])

  useEffect(() => {
    /**
     * The errorMessage value is only set when the form failed to submit previously
     * Whenever loading is true, we can clear this value
     */
    if (loading && errorMessage && !error?.message) {
      setErrorMessage(null)
    }
  }, [loading, error?.message, errorMessage])

  const onClickSubmit = useCallback(() => {
    trackEvent({
      id: 'app.userFeedback.survey.submitButton.clicked',
      description: 'Tracks whenever the user feedback submit button is clicked',
    })
    submitSurvey()
  }, [submitSurvey, trackEvent])

  const onClickCancel = useCallback(() => {
    trackEvent({
      id: 'app.userFeedback.survey.cancelButton.clicked',
      description: 'Tracks whenever the user feedback cancel button is clicked',
    })
    toolProps?.onClose()
  }, [toolProps, trackEvent])

  return (
    <>
      <ModalHeader useTDS={true}>{messages.title}</ModalHeader>
      <ModalBody>
        <SurveyInputContainer>
          <SurveyHelpText>{messages.textAreaHelpText}</SurveyHelpText>
          <SurveyHelpText>
            {messages.textAreaSupportText}
            <Link
              href="https://instacart.atlassian.net/servicedesk/customer/portal/1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {messages.textAreaSupportTextLink}
            </Link>
            .
          </SurveyHelpText>
          <SurveyHelpText>
            {messages.textAreaCustomerSupportText}
            <Link
              href="https://partner-docs.instacart.com/instacart_platform_portal/get_started/contact_support/#instacart-retail-support"
              target="_blank"
              rel="noopener noreferrer"
            >
              {messages.textAreaCustomerSupportLink}
            </Link>
            .
          </SurveyHelpText>
          <SurveySubheading>{messages.textAreaSubheading}</SurveySubheading>
          <TextArea
            data-testid="user-feedback-text-area"
            onChange={evt => updateSurveyState('userFeedbackResponse', evt.currentTarget.value)}
            css={{ minHeight: 125, fontSize: 15 }}
            placeholder={messages.textAreaPlaceholder}
            autoFocus
          />
          {errorMessage && <FormFieldError>{errorMessage}</FormFieldError>}
        </SurveyInputContainer>
        <SurveyInputContainer>
          <CheckboxWithLabel
            data-testid="user-feedback-checkbox"
            id={messages.checkboxLabel}
            label={messages.checkboxLabel}
            checked={surveyState.userFeedbackAgreeContact}
            onChange={evt =>
              updateSurveyState('userFeedbackAgreeContact', evt.currentTarget.checked)
            }
          />
        </SurveyInputContainer>
      </ModalBody>
      <ModalFooter>
        <ButtonsSection css={{ marginTop: 0, columnGap: '8px' }}>
          <Button
            variant="secondary"
            onClick={onClickCancel}
            data-testid="user-feedback-secondary-btn"
          >
            {messages.secondaryButtonText}
          </Button>
          <Button
            variant="primary"
            disabled={isButtonDisabled}
            loading={loading}
            onClick={onClickSubmit}
            data-testid="user-feedback-primary-btn"
          >
            {messages.primaryButtonText}
          </Button>
        </ButtonsSection>
      </ModalFooter>
    </>
  )
}

export default UserFeedbackSurveyPage1
